import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, User, ArrowLeft
} from 'lucide-react';

// Clean Slate Theme - Professional, distraction-free, high clarity
// Colors: Dark Slate primary (#0F172A), Blue accent (#3B82F6)
// Font: Inter (headings & body)
// Style: Soft shadows, minimal borders, subtle rounded corners

const CleanSlatePreview = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: '#FFFFFF',
      fontFamily: "'Inter', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white rounded-lg p-2 shadow-md hover:shadow-lg transition-shadow"
      >
        <ArrowLeft className="w-5 h-5 text-slate-700" />
      </button>

      {/* Theme Label */}
      <div className="fixed top-4 right-4 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-lg">
        Theme 2: Clean Slate
      </div>

      {/* Hero Header */}
      <div className="relative" style={{ backgroundColor: '#0F172A' }}>
        <div className="px-4 pt-16 pb-8">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-slate-900 font-bold text-lg">N</span>
                </div>
                <div className="text-white">
                  <p className="text-xs text-slate-400 font-medium">Newark SYEP</p>
                  <p className="text-sm font-semibold">Summer 2026</p>
                </div>
              </div>
              <button className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Welcome Section */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-slate-700 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">B</span>
              </div>
              <div className="text-white">
                <h1 className="text-2xl font-bold tracking-tight">
                  Welcome back, Brandon
                </h1>
                <p className="text-slate-400 text-sm mt-1">Let's continue your journey</p>
              </div>
            </div>

            {/* Worksite Info */}
            <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wide">Current Worksite</p>
                  <p className="text-white text-sm font-medium">Newark Public Library</p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Points Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Points</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">1,250</p>
              </div>

              {/* Streak Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Current Streak</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">7 <span className="text-lg font-normal text-slate-400">days</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-8 space-y-8">
        
        {/* Quick Actions */}
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Quick Actions</h2>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Target, label: 'Goals', color: 'bg-emerald-50 text-emerald-600' },
              { icon: Briefcase, label: 'Project', color: 'bg-orange-50 text-orange-600' },
              { icon: Trophy, label: 'Rankings', color: 'bg-yellow-50 text-yellow-600' },
              { icon: Award, label: 'Wins', color: 'bg-pink-50 text-pink-600' },
              { icon: FolderOpen, label: 'Resources', color: 'bg-amber-50 text-amber-600' },
              { icon: HelpCircle, label: 'Help', color: 'bg-blue-50 text-blue-600' },
            ].map((item) => (
              <button
                key={item.label}
                className="bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <p className="text-xs font-medium text-slate-700 text-center">{item.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Work Journal Card */}
        <div className="bg-slate-50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Work Journal</h2>
              <p className="text-slate-500 text-sm">Document your weekly progress</p>
            </div>
          </div>
          <button className="w-full bg-slate-900 text-white rounded-lg py-3.5 font-medium hover:bg-slate-800 transition-colors">
            Add Entry (+50 pts)
          </button>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">This Week's Tasks</h2>
            <Calendar className="w-4 h-4 text-slate-400" />
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div className="bg-blue-600 rounded-xl p-4 cursor-pointer hover:bg-blue-700 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-semibold">Skills Assessment</h3>
                    <p className="text-blue-100 text-sm">Track your growth • 50 pts</p>
                  </div>
                </div>
                <span className="bg-white text-blue-600 text-xs font-semibold px-2.5 py-1 rounded-md">New</span>
              </div>
            </div>

            {/* Check-in Card */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Weekly Check-in</h3>
                    <p className="text-slate-500 text-sm">Share your experience • 20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </div>

            {/* Trivia Card */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Weekly Trivia</h3>
                    <p className="text-slate-500 text-sm">Test your knowledge • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div className="bg-slate-900 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-300" />
              </div>
              <div className="text-white">
                <h3 className="font-semibold">Team Alpha</h3>
                <p className="text-slate-400 text-sm">8 members</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">4,500</p>
              <p className="text-slate-500 text-xs">team pts</p>
            </div>
          </div>
          <button className="w-full bg-slate-800 hover:bg-slate-700 rounded-lg py-3 text-white font-medium transition-colors">
            View Team <ChevronRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2">
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: '🏠', label: 'Home', active: true },
            { icon: '🎯', label: 'Goals', active: false },
            { icon: '📊', label: 'Progress', active: false },
            { icon: '👤', label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                item.active 
                  ? 'text-slate-900' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className={`text-xs ${item.active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
              {item.active && <div className="w-1 h-1 bg-blue-500 rounded-full mt-0.5" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CleanSlatePreview;
